import React, { createRef, PureComponent } from "react";
import type { MentionCategory, MentionReference, SelectedElementAttachment } from "../shared/mcp-chat-types";

interface ContentEditableInputProps {
  placeholder: string;
  readOnly: boolean;
  onInput: (plainText: string, mentions: MentionReference[]) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onSelect?: () => void;
  className?: string;
}

interface SerializeResult {
  plainText: string;
  mentions: MentionReference[];
  elements: SelectedElementAttachment[];
}

class ContentEditableInput extends PureComponent<ContentEditableInputProps> {
  private editorRef = createRef<HTMLDivElement>();
  private isComposing = false;

  // --- Imperative API (public methods) ---

  focus = (): void => {
    const el = this.editorRef.current;
    if (!el) return;
    el.focus();
    // Place cursor at end
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  resetHeight = (): void => {
    const el = this.editorRef.current;
    if (!el) return;
    el.style.height = "40px";
  };

  getElement = (): HTMLDivElement | null => {
    return this.editorRef.current;
  };

  clear = (): void => {
    const el = this.editorRef.current;
    if (!el) return;
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
    el.style.height = "40px";
  };

  setText = (text: string): void => {
    const el = this.editorRef.current;
    if (!el) return;
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
    el.textContent = text;
    // Grow without the 40px reset to avoid visible flicker during streaming
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
    // Scroll to bottom so the latest content is visible (e.g. during improve prompt streaming)
    el.scrollTop = el.scrollHeight;
  };

  insertMention = (mention: MentionReference): void => {
    const el = this.editorRef.current;
    if (!el) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);

    // Delete the @query text before cursor
    const textNode = range.startContainer;
    if (textNode.nodeType === Node.TEXT_NODE && textNode.textContent) {
      const text = textNode.textContent;
      const cursorOffset = range.startOffset;
      // Find the @ symbol before cursor
      const beforeCursor = text.substring(0, cursorOffset);
      const atIndex = beforeCursor.lastIndexOf("@");
      if (atIndex !== -1) {
        // Remove the @query text
        const before = text.substring(0, atIndex);
        const after = text.substring(cursorOffset);
        textNode.textContent = before + after;

        // Set range to insertion point
        range.setStart(textNode, atIndex);
        range.setEnd(textNode, atIndex);
      }
    }

    // Insert chip element
    const chip = this.createChipElement(mention);
    range.insertNode(chip);

    // Add a space after the chip
    const space = document.createTextNode("\u00A0");
    if (chip.nextSibling) {
      chip.parentNode?.insertBefore(space, chip.nextSibling);
    } else {
      chip.parentNode?.appendChild(space);
    }

    // Place cursor after the space
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    this.autoGrow();
    this.emitChange();
  };

  insertElementPill = (element: SelectedElementAttachment): void => {
    const el = this.editorRef.current;
    if (!el) return;

    el.focus();

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      // Place cursor at end if no selection
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    const range = sel!.getRangeAt(0);

    const pill = this.createElementPillNode(element);
    range.collapse(false);
    range.insertNode(pill);

    // Add a space after the pill
    const space = document.createTextNode("\u00A0");
    if (pill.nextSibling) {
      pill.parentNode?.insertBefore(space, pill.nextSibling);
    } else {
      pill.parentNode?.appendChild(space);
    }

    // Place cursor after the space
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel!.removeAllRanges();
    sel!.addRange(newRange);

    this.autoGrow();
    this.emitChange();
  };

  insertTextAtCursor = (text: string): void => {
    const el = this.editorRef.current;
    if (!el) return;

    el.focus();
    document.execCommand("insertText", false, text);
  };

  getPlainText = (): string => {
    const el = this.editorRef.current;
    if (!el) return "";
    return this.serialize(el).plainText;
  };

  getCaretRect = (): DOMRect | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0).cloneRange();
    range.collapse(true);
    const rects = range.getClientRects();
    if (rects.length > 0) return rects[0];
    // Fallback: insert zero-width span, measure, remove
    const span = document.createElement("span");
    span.textContent = "\u200B";
    range.insertNode(span);
    const rect = span.getBoundingClientRect();
    span.parentNode?.removeChild(span);
    sel.getRangeAt(0).startContainer.parentNode?.normalize();
    return rect;
  };

  // --- Private helpers ---

  private createChipElement(mention: MentionReference): HTMLSpanElement {
    const chip = document.createElement("span");
    chip.contentEditable = "false";
    chip.className = "mcp-chat__mention-chip";
    chip.dataset.mentionId = String(mention.id);
    chip.dataset.mentionCategory = mention.category;
    chip.dataset.mentionEntityId = mention.entityId;
    chip.dataset.mentionDisplay = mention.displayName;
    chip.textContent = `@${mention.displayName}`;
    return chip;
  }

  private createElementPillNode(element: SelectedElementAttachment): HTMLSpanElement {
    const pill = document.createElement("span");
    pill.contentEditable = "false";
    pill.className = "mcp-chat__element-pill";
    pill.dataset.elementKuikaId = element.kuikaId;
    pill.dataset.elementComponentName = element.componentName;
    pill.dataset.elementDisplayName = element.displayName;

    // Inspector icon
    const icon = document.createElement("span");
    icon.className = "mcp-chat__element-pill-icon";
    icon.innerHTML =
      '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="9"/>' +
      '<line x1="22" y1="12" x2="18" y2="12"/>' +
      '<line x1="6" y1="12" x2="2" y2="12"/>' +
      '<line x1="12" y1="6" x2="12" y2="2"/>' +
      '<line x1="12" y1="22" x2="12" y2="18"/>' +
      '<circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>' +
      "</svg>";
    pill.appendChild(icon);

    const label = document.createTextNode(element.displayName);
    pill.appendChild(label);

    return pill;
  }

  private serialize(container: HTMLElement): SerializeResult {
    let plainText = "";
    const mentions: MentionReference[] = [];
    const elements: SelectedElementAttachment[] = [];
    const seenElementIds = new Set<string>();

    const walkNodes = (parent: Node, prependNewline: boolean): void => {
      const children = parent.childNodes;
      for (let i = 0; i < children.length; i++) {
        const node = children[i];

        if (node.nodeType === Node.TEXT_NODE) {
          // Replace non-breaking spaces with regular spaces
          plainText += (node.textContent || "").replace(/\u00A0/g, " ");
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;

          if (el.classList.contains("mcp-chat__element-pill")) {
            const kuikaId = el.dataset.elementKuikaId || "";
            if (kuikaId && !seenElementIds.has(kuikaId)) {
              seenElementIds.add(kuikaId);
              elements.push({
                kuikaId,
                componentName: el.dataset.elementComponentName || "",
                displayName: el.dataset.elementDisplayName || ""
              });
            }
            // Pills are surfaced as structured attachments \u2014 they no longer leak
            // into plainText. The trailing space inserted with the pill at
            // creation time keeps surrounding text readable.
          } else if (el.classList.contains("mcp-chat__mention-chip")) {
            // Extract mention from dataset
            const ref: MentionReference = {
              id: parseInt(el.dataset.mentionId || "0", 10),
              category: (el.dataset.mentionCategory || "component") as MentionCategory,
              entityId: el.dataset.mentionEntityId || "",
              displayName: el.dataset.mentionDisplay || ""
            };
            mentions.push(ref);
            plainText += `@[${ref.displayName}]`;
          } else if (el.tagName === "BR") {
            plainText += "\n";
          } else if (el.tagName === "DIV") {
            // DIV elements are created by pressing Enter
            if (prependNewline || i > 0) {
              plainText += "\n";
            }
            walkNodes(el, false);
          } else {
            // Recurse into other elements
            walkNodes(el, false);
          }
        }
      }
    };

    walkNodes(container, false);
    return { plainText, mentions, elements };
  }

  getSerializedContent = (): SerializeResult => {
    const el = this.editorRef.current;
    if (!el) return { plainText: "", mentions: [], elements: [] };
    return this.serialize(el);
  };

  private cleanEmptyContent = (): void => {
    const el = this.editorRef.current;
    if (!el) return;
    // When user deletes all text, browsers leave behind a <br> or empty nodes.
    // This prevents :empty from matching, so the CSS placeholder won't show.
    // Clean up so the div is truly empty.
    const text = el.textContent || "";
    if (
      text.trim() === "" &&
      !el.querySelector(".mcp-chat__mention-chip") &&
      !el.querySelector(".mcp-chat__element-pill")
    ) {
      while (el.firstChild) {
        el.removeChild(el.firstChild);
      }
    }
  };

  private autoGrow = (): void => {
    const el = this.editorRef.current;
    if (!el) return;
    el.style.height = "40px";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  private emitChange = (): void => {
    const el = this.editorRef.current;
    if (!el) return;
    const { plainText, mentions } = this.serialize(el);
    this.props.onInput(plainText, mentions);
  };

  // --- Event handlers ---

  private handleInput = (): void => {
    if (this.isComposing) return;
    this.cleanEmptyContent();
    this.autoGrow();
    this.emitChange();
  };

  private handleCompositionStart = (): void => {
    this.isComposing = true;
  };

  private handleCompositionEnd = (): void => {
    this.isComposing = false;
    this.autoGrow();
    this.emitChange();
  };

  private handlePasteInternal = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    // Call parent's onPaste first
    this.props.onPaste(e);

    // If parent didn't prevent default, paste as plain text
    if (!e.defaultPrevented) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    }
  };

  // --- Render ---

  render(): React.ReactNode {
    const { readOnly, placeholder, onKeyDown, className } = this.props;

    return (
      <div
        ref={this.editorRef}
        className={`mcp-chat__content-editable ${className || ""}`}
        contentEditable={!readOnly}
        role="textbox"
        aria-label="Chat message input"
        aria-placeholder={placeholder}
        data-placeholder={placeholder}
        data-testid="mcpChatInput"
        suppressContentEditableWarning
        onInput={this.handleInput}
        onKeyDown={onKeyDown}
        onPaste={this.handlePasteInternal}
        onSelect={this.props.onSelect}
        onCompositionStart={this.handleCompositionStart}
        onCompositionEnd={this.handleCompositionEnd}
      />
    );
  }
}

export default ContentEditableInput;
